import { createSlice } from '@reduxjs/toolkit';

/**
 * @typedef {import('BMapsModel').Compound} Compound
 * @typedef {import('BMapsModel').MapCase} MapCase
 *
 * An individual structure in the target list. isTarget is the binary
 * target/off-target flag. source records how the entry was added (optional).
 * alignTransform/alignReferenceFrame are present only for an unloaded structure of
 * interest whose alignment was resolved at add time (raw USAlign transform + the
 * reference structureKey it targets); see redux/projectState/access.getTargetInfoForMapCase.
 * @typedef {{
 *   mapCase: MapCase, displayName: string, isTarget: boolean,
 *   source?: 'manual'|'query'|'curated',
 *   alignTransform?: { rotation: number[][], translation: number[] },
 *   alignReferenceFrame?: string,
 * }} IndividualTarget
 *
 * A named group of structures treated as a single target-list entry (e.g. a curated
 * kinase conformation panel). groupId is the stable key. Members are expected to be
 * unique by construction (curated panels come from per-structure lists); the store
 * does not dedup them. A group has NO target/off-target flag for now — its members are
 * scored only when pulled out as individuals. (A group-level flag, or a per-entry role,
 * can be added later.)
 * @typedef {{
 *   groupId: string, mapCases: MapCase[], displayName: string,
 *   source?: 'manual'|'query'|'curated',
 * }} GroupTarget
 *
 * @typedef {IndividualTarget | GroupTarget} TargetEntry
 * @typedef {TargetEntry[]} structList
 * @typedef {{
 *    proteinFamily: string,
 *    targetList: structList,
 *    scoringTableState: {
 *        columnVisibilityModel: import('@mui/x-data-grid').GridColumnVisibilityModel
 *        scoring: Boolean,
 *        sidePanelWidth: number,
 *        activeChart: string,
 *        chartState: object,
 *    }
 *    compoundTableState: {
 *        columnVisibilityModel: import('@mui/x-data-grid').GridColumnVisibilityModel
 *        sidePanelWidth: number,
 *        activeChart: string,
 *        chartState: object,
 *    }
 *    infoDisplayPanels: { selector: { open: boolean }, inspector: { open: boolean } }
 * }} initialState
 */

const initialTableState = {
    scoringTable: {
        scoring: false,
        sidePanelWidth: 600,
        activeChart: 'bmapsTable',
        chartState: {
            coralMap: {
                activeCompounds: [],
                colorScheme: null,
                property: null,
                propertyThresholds: {},
                propertyPreferences: {},
            },
        },
    },
    compoundTable: {
        sidePanelWidth: 600,
        activeChart: 'bmapsTable',
    },
    freeEnergyTable: {
        sidePanelWidth: 760,
        activeChart: 'bmapsTable',
        columnWidths: {},
    },
    mdTable: {
        sidePanelWidth: 780,
        activeChart: 'bmapsTable',
        columnWidths: {},
    },
    screeningTable: {
        sidePanelWidth: 800,
        activeChart: 'bmapsTable',
        chartState: {
            coralMap: {
                activeProperty: null,
                propertySettings: {},
            },
        },
        /** @type {{smiles: string, baseHue: number, name: string, properties:{}}[]} */
        screeningCompoundInfoList: [],
    },
    jobsGridTable: {

    },
};

// Side panel COLUMNS, keyed by side. Deliberately not part of `tableState`:
// every key there is a real table whose side panel belongs to it, and a column
// hosting Help / Workflow / Results is not a table. It also keeps this out of
// SavedState, which serializes tableState into the session file - a personal
// panel width does not belong in a project someone might share. Durable
// persistence is localStorage, in SidePanelLayout.
// Entries are created on first write; see setSidePanelPaneWidth.
const initialSidePanels = {};

// The InfoDisplay overlay's two collapsible panels (Selector, Inspector). Lives here rather
// than in the workspace DisplayState so that a workspace reset (loading a new protein, Views ->
// Reset) leaves it alone -- it is panel layout, not how the molecule is drawn. Unlike
// `sidePanels` it IS written to the session file, explicitly, by SavedState (see
// labwork/studies/selector-inspector-collapse, approach D3).
const initialInfoDisplayPanels = {
    selector: { open: true },
    inspector: { open: true },
};

/** @type {initialState} */
export const initialState = {
    proteinFamily: '',
    targetList: [],
    tableState: initialTableState,
    sidePanels: initialSidePanels,
    infoDisplayPanels: initialInfoDisplayPanels,
};

const slice = createSlice({
    name: 'projectState',
    initialState,
    reducers: {
        addStructure(state, action) {
            // ensure that target id is unique.
            if (state.targetList.some((value) => value.mapCase === action.payload.mapCase)) {
                return ({ ...state });
            }
            return {
                ...state,
                targetList: [
                    ...state.targetList,
                    action.payload,
                ],
            };
        },
        changeTarget(state, action) {
            const newState = state.targetList.map((value) => {
                if (action.payload.mapCase === value.mapCase) {
                    return ({
                        ...value,
                        isTarget: !value.isTarget,
                    });
                } else {
                    return (value);
                }
            });
            return {
                ...state,
                targetList: [...newState],
            };
        },
        setProteinTarget(state, action) {
            const newState = state.targetList.map((value) => {
                if (action.payload.mapCase === value.mapCase) {
                    return (action.payload);
                } else {
                    return (value);
                }
            });
            return {
                ...state,
                targetList: [...newState],
            };
        },
        updateStructureAlignment(state, action) {
            const { mapCase, alignTransform, alignReferenceFrame } = action.payload;
            return {
                ...state,
                targetList: state.targetList.map((value) => (
                    value.mapCase === mapCase
                        ? { ...value, alignTransform, alignReferenceFrame }
                        : value
                )),
            };
        },
        setAllTargetInfo(state, action) {
            return {
                ...state,
                targetList: [...action.payload],
            };
        },
        removeStructure(state, action) {
            const newState = state.targetList.filter(
                (value) => value.mapCase !== action.payload.mapCase
            );
            return {
                ...state,
                targetList: [...newState],
            };
        },
        addGroup(state, action) {
            // ensure that group id is unique.
            if (state.targetList.some((value) => value.groupId === action.payload.groupId)) {
                return ({ ...state });
            }
            return {
                ...state,
                targetList: [...state.targetList, action.payload],
            };
        },
        removeGroup(state, action) {
            return {
                ...state,
                targetList: state.targetList.filter(
                    (value) => value.groupId !== action.payload.groupId
                ),
            };
        },
        changeProteinFamily(state, action) {
            return {
                ...state,
                proteinFamily: action.payload,
            };
        },
        setTableState(state, action) {
            state.tableState = { ...state.tableState, ...action.payload };
        },
        setCompoundTableState(state, action) {
            state.tableState.compoundTable = {
                ...state.tableState.compoundTable,
                ...action.payload,
            };
        },
        setScoringTableState(state, action) {
            state.tableState.scoringTable = {
                ...state.tableState.scoringTable,
                ...action.payload,
            };
        },
        setJobsCompoundsTableState(state, action) {
            state.tableState.jobsCompoundsTable = {
                ...state.tableState.jobsCompoundsTable,
                ...action.payload,
            };
        },
        setJobsGridTableState(state, action) {
            state.tableState.jobsGridTable = {
                ...state.tableState.jobsGridTable,
                ...action.payload,
            };
        },
        setScreeningTableState(state, action) {
            state.tableState.screeningTable = {
                ...state.tableState.screeningTable,
                ...action.payload,
            };
        },
        setFreeEnergyTableState(state, action) {
            state.tableState.freeEnergyTable = {
                ...state.tableState.freeEnergyTable,
                ...action.payload,
            };
        },
        setTargetsTableState(state, action) {
            state.tableState.targetsTable = {
                ...state.tableState.targetsTable,
                ...action.payload,
            };
        },
        setCoralMapState(state, action) {
            state.tableState[action.payload.table].chartState.coralMap = {
                ...state.tableState[action.payload.table].chartState.coralMap,
                ...action.payload.coralMap,
            };
        },
        updateScreeningPropertySettings(state, action) {
            state.tableState.screeningTable.chartState.coralMap.propertySettings = {
                ...state.tableState.screeningTable.chartState.coralMap.propertySettings,
                ...action.payload,
            };
        },
        setActiveChart(state, action) {
            state.tableState[action.payload.table].activeChart = action.payload.chart;
        },
        setSidePanelWidth(state, action) {
            state.tableState[action.payload.table].sidePanelWidth = action.payload.sidePanelWidth;
        },
        setSidePanelMinimized(state, action) {
            const { side, minimized } = action.payload;
            if (!state.sidePanels[side]) state.sidePanels[side] = { panes: {} };
            state.sidePanels[side].minimized = !!minimized;
        },
        // A companion `setSidePanelPaneMinimized` exists in the fork
        // (jfreeze95 fd0ee0943, PR #50) and was not migrated — nothing calls it,
        // and rail mode is column-level by design. See
        // docs/gibbs-migration-register.md before adding per-pane minimize.
        // Per-pane width for a stackable column. `setSidePanelWidth` above is
        // a different thing and stays on tableState: it is the width of the
        // panel attached to one table (CompoundTableSidePanel, ScoringTable-
        // SidePanel, ScreeningSidePanel), which genuinely is table state.
        setSidePanelPaneWidth(state, action) {
            const { side, paneId, width } = action.payload;
            if (!state.sidePanels[side]) state.sidePanels[side] = { panes: {} };
            if (!state.sidePanels[side].panes) state.sidePanels[side].panes = {};
            if (!state.sidePanels[side].panes[paneId]) {
                state.sidePanels[side].panes[paneId] = { width: 0 };
            }
            state.sidePanels[side].panes[paneId].width = width;
        },
        setInfoDisplayPanelOpen(state, action) {
            const { panel, open } = action.payload;
            if (!state.infoDisplayPanels[panel]) return;
            state.infoDisplayPanels[panel].open = !!open;
        },
        removeAllStructures(state) {
            return {
                ...state,
                targetList: [],
            };
        },
        reset() {
            return initialState;
        },
    },
});

export default slice;
